import { Link, Outlet, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  LogIn,
  Pause,
  Play,
  Mail,
  Copy,
} from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { TabBar, PropertyChips, type TabItem } from '@/components/ui/Tabs';
import { useAuditEntries, useAppendAudit } from '@/api/audit';
import { useTenant as useApiTenant, usePatchTenant } from '@/api/tenants';
import { tenantDetailToTenant, workspaceSummaryToWorkspace } from '@/api/adapters';
import { toast } from '@/components/ui/Toaster';
import type { Tenant, Workspace, StaffAuditEntry } from '@/data/types';
import { TENANT_STATUS_LABEL, TENANT_STATUS_TONE, REGION_LABEL } from '@/lib/tier';

export function TenantDetailLayout() {
  const { slug } = useParams();
  const { data: apiTenant, isLoading: apiLoading } = useApiTenant(slug);

  const tenant: Tenant | undefined = apiTenant ? tenantDetailToTenant(apiTenant) : undefined;
  const workspaces: Workspace[] = apiTenant
    ? apiTenant.workspaces.map(w => workspaceSummaryToWorkspace(w, apiTenant.id, apiTenant.tier))
    : [];

  const { data: apiAudit } = useAuditEntries();
  const allAudit: StaffAuditEntry[] = (apiAudit ?? []).map(e => ({
    id: e.id,
    timestamp: e.timestamp,
    staffName: e.staff_name,
    staffEmail: e.staff_email,
    action: e.action,
    target: e.target,
    tenantId: e.tenant_id ?? undefined,
    tenantName: e.tenant_name ?? undefined,
    ip: e.ip,
    result: e.result,
    reason: e.reason ?? undefined,
  }));
  const appendApiAudit = useAppendAudit();
  const patchTenant = usePatchTenant();

  async function setTenantStatus(status: 'active' | 'suspended') {
    if (!tenant) return;
    const verb = status === 'suspended' ? 'Suspend' : 'Reactivate';
    if (!window.confirm(
      `${verb} ${tenant.name}'s account?` +
      (status === 'suspended'
        ? ' This marks the account suspended for billing/CRM purposes. Their instances keep running — suspend those individually if needed.'
        : ''),
    )) return;
    try {
      await patchTenant.mutateAsync({ tenantId: tenant.slug, status });
      toast.success(`${tenant.name} ${status === 'suspended' ? 'suspended' : 'reactivated'}`);
    } catch (e: any) {
      toast.danger(`${verb} failed`, e?.message ?? 'Unknown error');
    }
  }

  function viewAsCustomer() {
    if (!tenant) return;
    const reason = window.prompt(
      `Opening ${tenant.name} as a customer. Why?\n\n` +
      `Phase 0: this opens the workspace URL in a new tab — you'll need to log in normally.\n` +
      `Phase 1 will pass a signed operator JWT that the NousViz app accepts as a read-only session (see docs/view-as-customer.md).`,
      'Support ticket #',
    );
    if (!reason) return;
    const ws = workspaces[0];
    appendApiAudit.mutate({
      action: 'tenant.view_as_customer',
      target: `Opened ${tenant.name} (${ws?.url ?? 'no workspace'}) as operator`,
      tenant_id: tenant.id,
      tenant_slug: tenant.slug,
      tenant_name: tenant.name,
      reason,
    });
    toast.info('View-as-customer logged', `Audited. Phase 1 adds session impersonation; Phase 0 opens the URL.`);
    if (ws?.url) window.open(ws.url, '_blank', 'noopener');
  }

  if (!tenant) {
    return (
      <PageBody>
        <Card className="p-8 text-center">
          <div className="text-md font-semibold">
            {apiLoading ? 'Loading…' : 'Customer not found'}
          </div>
          {!apiLoading && (
            <>
              <p className="text-sm text-fg-muted mt-1">
                No tenant with slug <code className="font-mono">{slug}</code> in the registry.
              </p>
              <Link to="/tenants" className="text-accent text-sm mt-3 inline-block">
                ← Back to customers
              </Link>
            </>
          )}
        </Card>
      </PageBody>
    );
  }

  const audit = allAudit.filter(a => a.tenantId === tenant.id);
  const suspended = tenant.status === 'suspended';

  const base = `/tenants/${tenant.slug}`;
  const tabs: TabItem[] = [
    { to: base, label: 'Overview', end: true },
    { to: `${base}/workspaces`, label: 'Instances', count: workspaces.length },
    { to: `${base}/events`, label: 'Activity', count: audit.length },
    { to: `${base}/logs`, label: 'Logs' },
    { to: `${base}/settings`, label: 'Settings' },
  ];

  return (
    <>
      <div className="px-4 md:px-6 lg:px-8 pt-5 pb-4 bg-bg-elevated border-b border-border">
        <Link to="/tenants" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg mb-3">
          <ArrowLeft size={14} /> Customers
        </Link>

        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{tenant.name}</h1>
              <Badge tone={TENANT_STATUS_TONE[tenant.status]}>
                {TENANT_STATUS_LABEL[tenant.status]}
              </Badge>
              <button
                className="text-fg-subtle hover:text-fg ml-1 inline-flex items-center gap-1 text-2xs font-mono"
                onClick={() => navigator.clipboard?.writeText(tenant.id)}
                title="Copy customer ID"
              >
                <Copy size={11} /> {tenant.id}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <a href={`mailto:${tenant.ownerEmail}`} title={`Email ${tenant.ownerName} (${tenant.ownerEmail})`}>
              <Button variant="ghost" size="sm" iconLeft={<Mail size={13} />}>Email</Button>
            </a>
            {suspended ? (
              <Button
                variant="secondary" size="sm" iconLeft={<Play size={13} />}
                title="Set the customer's account back to active"
                onClick={setTenantStatus.bind(null, 'active')}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                variant="secondary" size="sm" iconLeft={<Pause size={13} />}
                title="Mark the account suspended (e.g. non-payment). Doesn't take their instances offline — do that per instance."
                onClick={setTenantStatus.bind(null, 'suspended')}
              >
                Suspend
              </Button>
            )}
            <Button
              variant="primary" size="sm" iconLeft={<LogIn size={13} />} onClick={viewAsCustomer}
              title="Open the customer's site in a new tab (audit-logged)"
            >
              View as customer
            </Button>
          </div>
        </div>

        <PropertyChips
          chips={[
            { label: 'Owner', value: tenant.ownerName },
            { label: 'Email', value: tenant.ownerEmail, mono: true },
            { label: 'Region', value: REGION_LABEL[tenant.region] },
            { label: 'Instances', value: String(workspaces.length) },
            { label: 'Support owner', value: tenant.supportOwner ?? '—' },
            { label: 'Created', value: new Date(tenant.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) },
          ]}
        />
      </div>

      <TabBar tabs={tabs} />

      <Outlet context={{ tenant, workspaces, payments: [], audit }} />
    </>
  );
}

